<template>
  <v-card class="employee-timesheet">
    <v-card-title class="timesheet-header">
      <div class="timesheet-header__title">
        <v-icon size="20" class="mr-2">mdi-clock-outline</v-icon>
        <span class="text-subtitle-1 font-weight-medium">{{ title }}</span>
        <v-chip color="primary" class="ml-2">
          {{ formattedSelectedDate }}
        </v-chip>
      </div>
      <div class="timesheet-header__actions">
        <v-btn icon variant="text" size="small" @click="goToPreviousDay">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon variant="text" size="small" @click="goToNextDay">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn variant="text" size="small" @click="goToToday">{{ t("employeeTimesheet.today") }}</v-btn>
        <v-menu v-model="calendarMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
          <template #activator="{ props }">
            <v-btn v-bind="props" variant="text" size="small" prepend-icon="mdi-calendar-month-outline">
              {{ t("employeeTimesheet.pickDay") }}
            </v-btn>
          </template>
          <v-date-picker
            :model-value="selectedDate"
            color="primary"
            hide-header
            :weekdays="[1, 2, 3, 4, 5, 6, 0]"
            :first-day-of-week="1"
            @update:model-value="handleDateSelection"
          />
        </v-menu>
        <v-btn v-if="canManage" color="tertiary" size="small" prepend-icon="mdi-plus" class="ml-2" @click="openCreateDialog">
          {{ t("employeeTimesheet.addEvent") }}
        </v-btn>
        <v-btn color="primary" size="small" variant="outlined" prepend-icon="mdi-download-outline" class="ml-2" @click="openExportDialog">
          {{ t("employeeTimesheetHr.export.downloadTitle") }}
        </v-btn>
      </div>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="timesheet-body">
      <v-alert
        v-if="exportNotice"
        type="info"
        density="compact"
        class="mb-3"
      >
        {{ exportNotice }}
      </v-alert>
      <v-alert
        v-if="errorMessage"
        type="error"
        density="compact"
        class="mb-3"
      >
        {{ errorMessage }}
      </v-alert>

      <v-skeleton-loader
        v-if="loading"
        type="list-item-two-line"
        class="mb-4"
        :loading="loading"
        :rows="3"
      />

      <div v-else>
        <div v-if="events.length === 0" class="timesheet-empty">
          <v-icon size="22" class="timesheet-empty__icon mb-2">mdi-calendar-blank-outline</v-icon>
          <p class="text-body-2 text-medium-emphasis mb-1">{{ t("employeeTimesheet.empty.title") }}</p>
          <p v-if="canManage" class="text-caption text-medium-emphasis mb-3">
            {{ t("employeeTimesheet.empty.hint") }}
          </p>
          <v-btn v-if="canManage" size="small" variant="outlined" color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
            {{ t("employeeTimesheet.addEvent") }}
          </v-btn>
        </div>
        <v-list v-else class="timesheet-list" lines="two" density="comfortable">
          <v-list-item
            v-for="event in orderedEvents"
            :key="event.id"
            class="timesheet-list__item"
          >
            <template #prepend>
              <v-avatar
                size="36"
                :color="event.is_auto ? 'primary' : 'neutral'"
                variant="tonal"
                class="mr-3"
              >
                <v-icon>{{ eventTypeConfig(event.event_type).icon }}</v-icon>
              </v-avatar>
            </template>
            <template #title>
              <div class="d-flex align-center">
                <span class="font-weight-medium mr-2">{{ eventTypeConfig(event.event_type).label }}</span>
                <v-chip size="x-small" :color="event.is_auto ? 'primary' : 'neutral'">
                  {{ event.is_auto ? t("employeeTimesheet.auto") : t("employeeTimesheet.manual") }}
                </v-chip>
              </div>
            </template>
            <template #subtitle>
              <div class="timesheet-item__meta">
                <span><v-icon size="16" class="mr-1">mdi-clock-outline</v-icon>{{ formatEventTime(event.event_time) }}</span>
                <span v-if="event.source" class="ml-3 text-caption text-medium-emphasis">
                  {{ t("employeeTimesheet.source") }}: {{ formatSource(event.source) }}
                </span>
              </div>
            </template>
            <template #append>
              <div v-if="canManage" class="d-flex align-center">
                <v-btn icon size="small" variant="text" :aria-label="t('app.actions.edit')" :title="t('app.actions.edit')" @click="openEditDialog(event)">
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn icon size="small" variant="text" color="error" :aria-label="t('app.actions.delete')" :title="t('app.actions.delete')" @click="deleteEvent(event)">
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </template>
          </v-list-item>
        </v-list>
      </div>
    </v-card-text>
  </v-card>

  <v-dialog v-model="dialog.open" max-width="420px">
    <v-card>
      <v-card-title>{{ dialog.mode === 'create' ? t("employeeTimesheet.dialog.addTitle") : t("employeeTimesheet.dialog.editTitle") }}</v-card-title>
      <v-card-text>
        <v-form @submit.prevent="saveEvent">
          <v-select
            :label="t('employeeTimesheet.fields.type')"
            :items="eventTypeOptions"
            item-title="label"
            item-value="value"
            v-model="dialog.form.event_type"
            class="mb-3"
            required
          ></v-select>
          <v-menu
            v-model="timeMenu"
            :close-on-content-click="false"
            max-width="320"
            transition="scale-transition"
          >
            <template #activator="{ props }">
              <v-text-field
                v-bind="props"
                :label="t('employeeTimesheet.fields.time')"
                :model-value="dialog.form.time"
                readonly
                variant="outlined"
                density="comfortable"
                :rules="[timeFieldRule]"
                append-inner-icon="mdi-clock-outline"
              ></v-text-field>
            </template>
            <v-card>
              <v-time-picker
                v-model="timePickerValue"
                format="24hr"
                scrollable
                @update:model-value="onTimeSelected"
              />
              <v-card-actions class="justify-end">
                <v-btn variant="text" @click="timeMenu = false">{{ t("app.actions.cancel") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
          <v-switch
            v-if="dialog.mode === 'edit'"
            v-model="dialog.form.is_auto"
            inset
            color="primary"
            hide-details
            class="mt-3"
            :label="t('employeeTimesheet.fields.autoLabel')"
          ></v-switch>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="dialog.open = false">{{ t("app.actions.cancel") }}</v-btn>
        <v-btn color="primary" :loading="dialog.loading" @click="saveEvent">
          {{ t("app.actions.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="exportDialog.open" max-width="520px">
    <v-card>
      <v-card-title>{{ t("employeeTimesheetHr.export.title") }}</v-card-title>
      <v-card-text>
        <p class="text-body-2 mb-3">
          {{ t("employeeTimesheetHr.export.description") }}
        </p>
        <v-alert
          v-if="exportDialog.error"
          type="error"
          density="compact"
          class="mb-3"
        >
          {{ exportDialog.error }}
        </v-alert>
        <v-menu
          v-model="exportRangeMenu"
          :close-on-content-click="false"
          max-width="360"
          transition="scale-transition"
        >
          <template #activator="{ props: menuProps }">
            <div class="d-flex ga-2 flex-wrap w-100" v-bind="menuProps">
              <v-text-field
                class="flex-grow-1"
                :label="t('employeeTimesheetHr.export.from')"
                readonly
                :model-value="exportRangeDisplay.start"
                @click.stop="handleExportRangeControlClick('start', $event, menuProps)"
                @mousedown="handleExportRangePointerDown('start')"
                @focus="handleExportRangeFieldFocus('start')"
                @keydown.enter.prevent="handleExportRangeKeyboardOpen('start')"
                @keydown.space.prevent="handleExportRangeKeyboardOpen('start')"
                @click:control.stop="handleExportRangeControlClick('start', $event, menuProps)"
              ></v-text-field>
              <v-text-field
                class="flex-grow-1"
                :label="t('employeeTimesheetHr.export.to')"
                readonly
                :model-value="exportRangeDisplay.end"
                @click.stop="handleExportRangeControlClick('end', $event, menuProps)"
                @mousedown="handleExportRangePointerDown('end')"
                @focus="handleExportRangeFieldFocus('end')"
                @keydown.enter.prevent="handleExportRangeKeyboardOpen('end')"
                @keydown.space.prevent="handleExportRangeKeyboardOpen('end')"
                @click:control.stop="handleExportRangeControlClick('end', $event, menuProps)"
              ></v-text-field>
            </div>
          </template>
          <v-card>
            <v-date-picker
              :model-value="exportRangeDateModel"
              color="primary"
              range
              :first-day-of-week="1"
              show-adjacent-months
              @update:model-value="onExportRangeChange"
            ></v-date-picker>
            <v-card-actions class="justify-end">
              <v-btn variant="text" @click="exportRangeMenu = false">{{ t("app.actions.cancel") }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
        <v-select
          class="mt-4"
          :label="t('employeeTimesheetHr.export.format')"
          :items="exportFormats"
          item-title="title"
          item-value="value"
          v-model="exportDialog.format"
        ></v-select>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" :disabled="exportDialog.loading" @click="exportDialog.open = false">{{ t("app.actions.cancel") }}</v-btn>
        <v-btn color="primary" :loading="exportDialog.loading" @click="confirmExport">
          {{ t("employeeTimesheetHr.export.download") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import { defineComponent, computed, ref, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { VTimePicker } from "vuetify/labs/VTimePicker";
import { formatAsIsoDate, normalizeDateString, toDateObject } from "@/Utile/holidayFormatting.mjs";
import { formatDisplayDate, formatDisplayTime, formatDisplayWeekday } from "@/Utile/displayFormat.mjs";

const EVENT_TYPES = [
  { value: "intrare", labelKey: "employeeTimesheet.eventTypes.checkIn", icon: "mdi-login" },
  { value: "pauza_start", labelKey: "employeeTimesheet.eventTypes.breakStart", icon: "mdi-coffee" },
  { value: "pauza_end", labelKey: "employeeTimesheet.eventTypes.breakEnd", icon: "mdi-coffee-outline" },
  { value: "iesire", labelKey: "employeeTimesheet.eventTypes.checkOut", icon: "mdi-logout" },
];

const EVENT_SORT_ORDER = {
  intrare: 1,
  pauza_start: 2,
  pauza_end: 3,
  iesire: 4,
};

export default defineComponent({
  name: "EmployeeTimesheetHr",
  components: {
    VTimePicker,
  },
  props: {
    employeeId: {
      type: [Number, String],
      required: true,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    title: {
      type: String,
      default: "Pontaj zilnic",
    },
    canManage: {
      type: Boolean,
      default: false,
    },
    initialDate: {
      type: String,
      default: () => new Date().toISOString().split("T")[0],
    },
    employeeName: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const { t, locale } = useI18n();
    const events = ref([]);
    const loading = ref(false);
    const errorMessage = ref("");
    const selectedDate = ref(normalizeToDate(props.initialDate));
    const calendarMenu = ref(false);
    const exportNotice = ref(null);

    const dialog = ref({
      open: false,
      mode: "create",
      loading: false,
      form: {
        id: null,
        event_type: EVENT_TYPES[0].value,
        time: "09:00",
        is_auto: false,
        source: "manual",
      },
    });

    const timeMenu = ref(false);
    const timePickerValue = ref("09:00");

    const exportDialog = ref({
      open: false,
      loading: false,
      error: null,
      format: "pdf",
    });
    const exportRangeForm = ref(buildDefaultExportRange());
    const exportRangeMenu = ref(false);
    const exportRangeActiveEdge = ref("start");

    const selectedDateIso = computed(() => formatIsoDate(selectedDate.value));

    const formattedSelectedDate = computed(() => formatLongDate(selectedDate.value));

    const canManage = computed(() => props.canManage);
    const cardTitle = computed(() => props.title || t("employeeTimesheet.title"));

    const orderedEvents = computed(() =>
      [...events.value].sort((a, b) => {
        const orderA = EVENT_SORT_ORDER[a.event_type] ?? 99;
        const orderB = EVENT_SORT_ORDER[b.event_type] ?? 99;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        const timeA = a?.event_time ? new Date(a.event_time).getTime() : 0;
        const timeB = b?.event_time ? new Date(b.event_time).getTime() : 0;
        return timeA - timeB;
      })
    );

    const eventTypeOptions = computed(() =>
      EVENT_TYPES.map((et) => ({
        ...et,
        label: t(et.labelKey),
      }))
    );

    function eventTypeConfig(type) {
      const match = eventTypeOptions.value.find((item) => item.value === type);
      return match || { ...EVENT_TYPES[0], label: t(EVENT_TYPES[0].labelKey) };
    }

    const timeFieldRule = (value) => {
      if (!value) return t("employeeTimesheet.validation.timeRequired");
      return normalizeTimeInput(value) ? true : t("employeeTimesheet.validation.timeFormat");
    };

    function buildDefaultExportRange() {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        start_date: formatAsIsoDate(start),
        end_date: formatAsIsoDate(end),
      };
    }

    function formatSource(source) {
      switch (source) {
        case "auto_job":
          return t("employeeTimesheet.sources.autoJob");
        case "auto_ui":
          return t("employeeTimesheet.sources.autoUi");
        default:
          return t("employeeTimesheet.sources.manual");
      }
    }

    function normalizeToDate(value) {
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return new Date(value.getTime());
      }
      if (!value) {
        return new Date();
      }
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return new Date();
      }
      return parsed;
    }

    function formatLongDate(dateObj) {
      if (!dateObj) return "";
      const date = normalizeToDate(dateObj);
      if (Number.isNaN(date.getTime())) return "";
      return `${formatDisplayWeekday(date, locale?.value)}, ${formatDisplayDate(date)}`;
    }

    function formatIsoDate(dateObj) {
      if (!dateObj) return new Date().toISOString().split("T")[0];
      const date = normalizeToDate(dateObj);
      if (Number.isNaN(date.getTime())) return new Date().toISOString().split("T")[0];
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    function formatEventTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return formatDisplayTime(date);
    }

    function goToPreviousDay() {
      const date = normalizeToDate(selectedDate.value);
      date.setDate(date.getDate() - 1);
      selectedDate.value = date;
    }

    function goToNextDay() {
      const date = normalizeToDate(selectedDate.value);
      date.setDate(date.getDate() + 1);
      selectedDate.value = date;
    }

    function goToToday() {
      selectedDate.value = new Date();
    }

    function handleDateSelection(value) {
      calendarMenu.value = false;
      selectedDate.value = normalizeToDate(value);
    }

    function openExportDialog() {
      exportDialog.value = {
        open: true,
        loading: false,
        error: null,
        format: "pdf",
      };
      exportRangeForm.value = buildDefaultExportRange();
      exportRangeMenu.value = false;
      exportRangeActiveEdge.value = "start";
    }

    function onExportRangeChange(value) {
      const selection = normalizeExportRangeSelection(value);
      const targetEdge = exportRangeActiveEdge.value || "start";
      const currentStart = exportRangeForm.value.start_date;
      const currentEnd = exportRangeForm.value.end_date;

      if (targetEdge === "end") {
        const candidate = selection.hasEnd
          ? selection.end
          : selection.hasStart
          ? selection.start
          : "";

        if (candidate) {
          if (currentStart && currentStart > candidate) {
            exportRangeForm.value.start_date = candidate;
            exportRangeForm.value.end_date = currentStart;
          } else {
            exportRangeForm.value.end_date = candidate;
            if (!currentStart && selection.hasStart) {
              exportRangeForm.value.start_date = selection.start;
            }
          }

          if (exportRangeForm.value.start_date && exportRangeForm.value.end_date) {
            exportRangeMenu.value = false;
            exportRangeActiveEdge.value = "start";
          }
        }
        return;
      }

      const candidateStart = selection.hasStart
        ? selection.start
        : selection.hasEnd
        ? selection.end
        : "";

      if (candidateStart) {
        if (selection.hasStart && selection.hasEnd) {
          const nextStart = selection.start;
          const nextEnd = selection.end;
          if (nextStart <= nextEnd) {
            exportRangeForm.value.start_date = nextStart;
            exportRangeForm.value.end_date = nextEnd;
          } else {
            exportRangeForm.value.start_date = nextEnd;
            exportRangeForm.value.end_date = nextStart;
          }
          exportRangeMenu.value = false;
          exportRangeActiveEdge.value = "start";
        } else {
          exportRangeForm.value.start_date = candidateStart;
          if (currentEnd && currentEnd < candidateStart) {
            exportRangeForm.value.end_date = candidateStart;
          }
          if (!selection.hasEnd) {
            exportRangeActiveEdge.value = "end";
          }
        }
      }
    }

    function openCreateDialog() {
      dialog.value = {
        open: true,
        mode: "create",
        loading: false,
        form: {
          id: null,
          event_type: EVENT_TYPES[0].value,
          time: "09:00",
          is_auto: false,
          source: "manual",
        },
      };
      timePickerValue.value = dialog.value.form.time;
      timeMenu.value = false;
    }

    function openEditDialog(event) {
      dialog.value = {
        open: true,
        mode: "edit",
        loading: false,
        form: {
          id: event.id,
          event_type: event.event_type,
          time: extractTimePart(event.event_time),
          is_auto: Boolean(event.is_auto),
          source: event.source || "manual",
        },
      };
      timePickerValue.value = dialog.value.form.time;
      timeMenu.value = false;
    }

    function extractTimePart(value) {
      if (!value) return "09:00";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value.slice(11, 16);
      }
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    }

    function normalizeTimeInput(value) {
      if (value === null || value === undefined) return null;
      const raw = String(value).trim();
      if (!raw) return null;

      const colonMatch = raw.match(/^(\d{1,2}):(\d{1,2})$/);
      if (colonMatch) {
        const hours = Number(colonMatch[1]);
        const minutes = Number(colonMatch[2]);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }

      const digitsMatch = raw.match(/^(\d{3,4})$/);
      if (digitsMatch) {
        const digits = digitsMatch[1].padStart(4, "0");
        const hours = Number(digits.slice(0, 2));
        const minutes = Number(digits.slice(2, 4));
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }

      return null;
    }

    function onTimeSelected(value) {
      const normalized = normalizeTimeInput(value);
      if (normalized) {
        timePickerValue.value = normalized;
        dialog.value.form.time = normalized;
        timeMenu.value = false;
        return;
      }
    }

    async function fetchEvents() {
      if (!props.employeeId) return;
      loading.value = true;
      errorMessage.value = "";
      try {
        const { data } = await axios.get("/timesheet/events", {
          params: {
            employeeId: props.employeeId,
            date: selectedDateIso.value,
          },
        });
        events.value = Array.isArray(data?.events) ? data.events : [];
      } catch (error) {
        console.error("Eroare la încărcarea evenimentelor de pontaj:", error);
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t("employeeTimesheet.errors.load");
        errorMessage.value = message;
        events.value = [];
      } finally {
        loading.value = false;
      }
    }

    function buildEventDateTime(time) {
      if (!time) return `${selectedDateIso.value}T00:00:00`;
      const sanitized = time.length === 5 ? `${time}:00` : time;
      return `${selectedDateIso.value}T${sanitized}`;
    }

    async function saveEvent() {
      if (dialog.value.loading) return;
      const form = dialog.value.form;
      const normalizedTime = normalizeTimeInput(form.time);
      if (!form.event_type || !normalizedTime) {
        alert(t("employeeTimesheet.validation.timeRequired"));
        return;
      }
      dialog.value.loading = true;
      try {
        if (dialog.value.mode === "create") {
          await axios.post("/timesheet/events", {
            employee_id: Number(props.employeeId),
            event_type: form.event_type,
            event_time: buildEventDateTime(normalizedTime),
            source: "manual",
            is_auto: false,
          });
        } else {
          await axios.put(`/timesheet/events/${form.id}`, {
            event_type: form.event_type,
            event_time: buildEventDateTime(normalizedTime),
            is_auto: form.is_auto,
            source: form.source,
          });
        }
        dialog.value.open = false;
        await fetchEvents();
      } catch (error) {
        console.error("Eroare la salvarea evenimentului de pontaj:", error);
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t("employeeTimesheet.errors.save");
        alert(message);
      } finally {
        dialog.value.loading = false;
      }
    }

    async function deleteEvent(event) {
      if (!confirm(t("employeeTimesheet.confirmDelete"))) return;
      try {
        await axios.delete(`/timesheet/events/${event.id}`);
        await fetchEvents();
      } catch (error) {
        console.error("Eroare la ștergerea evenimentului de pontaj:", error);
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t("employeeTimesheet.errors.delete");
        alert(message);
      }
    }

    watch(
      () => [props.employeeId, selectedDateIso.value],
      () => fetchEvents(),
      { immediate: false }
    );

    watch(
      () => props.initialDate,
      (newDate) => {
        if (newDate) {
          selectedDate.value = normalizeToDate(newDate);
        }
      }
    );

    onMounted(() => {
      fetchEvents();
    });

    const exportFormats = [
      { title: "PDF", value: "pdf" },
      { title: "Excel (.xlsx)", value: "xlsx" },
    ];

    const exportRangeDisplay = computed(() => ({
      start: exportRangeForm.value.start_date || "",
      end: exportRangeForm.value.end_date || "",
    }));

    const exportRangeDateModel = computed(() => {
      const start = toDateObject(exportRangeForm.value.start_date);
      const end = toDateObject(exportRangeForm.value.end_date);

      if (start && end) {
        if (start <= end) {
          return [start, end];
        }
        return [end, start];
      }

      if (start) {
        return [start];
      }

      return [];
    });

    const normalizeExportRangeSelection = (value) => {
      if (!value) {
        return { start: "", end: "", hasStart: false, hasEnd: false };
      }

      let startValue = null;
      let endValue = null;

      if (Array.isArray(value)) {
        [startValue, endValue] = value;
      } else if (value instanceof Date || typeof value === "string") {
        startValue = value;
      } else if (typeof value === "object") {
        if ("start" in value || "end" in value) {
          startValue = value.start ?? null;
          endValue = value.end ?? null;
        }
      }

      const startIso = normalizeDateString(startValue);
      const endIso = normalizeDateString(endValue);

      if (startIso && endIso) {
        if (startIso <= endIso) {
          return { start: startIso, end: endIso, hasStart: true, hasEnd: true };
        }
        return { start: endIso, end: startIso, hasStart: true, hasEnd: true };
      }

      return {
        start: startIso || "",
        end: endIso || "",
        hasStart: Boolean(startIso),
        hasEnd: Boolean(endIso),
      };
    };

    const handleExportRangePointerDown = (edge) => {
      exportRangeActiveEdge.value = edge;
    };

    const handleExportRangeFieldFocus = (edge) => {
      exportRangeActiveEdge.value = edge;
    };

    const handleExportRangeKeyboardOpen = (edge) => {
      exportRangeActiveEdge.value = edge;
      exportRangeMenu.value = true;
    };

    const handleExportRangeControlClick = (edge, event, menuProps) => {
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      if (event && typeof event.stopPropagation === "function") event.stopPropagation();
      exportRangeActiveEdge.value = edge;
      exportRangeMenu.value = true;
      if (menuProps && typeof menuProps.onClick === "function") {
        menuProps.onClick(event);
      }
    };

    async function confirmExport() {
      const rangeStartIso = exportRangeForm.value.start_date;
      const rangeEndIso = exportRangeForm.value.end_date;
      const startDate = toDateObject(rangeStartIso);
      const endDate = toDateObject(rangeEndIso);

      if (!startDate || !endDate) {
        exportDialog.value.error = "Perioada selectată este invalidă.";
        return;
      }
      let rangeStart = startDate;
      let rangeEnd = endDate;
      if (rangeEnd < rangeStart) {
        const temp = rangeStart;
        rangeStart = rangeEnd;
        rangeEnd = temp;
      }
      exportDialog.value.loading = true;
      exportDialog.value.error = null;
      try {
        const payload = {
          employee_id: props.employeeId,
          start_date: formatAsIsoDate(rangeStart),
          end_date: formatAsIsoDate(rangeEnd),
          format: exportDialog.value.format,
        };
        const response = await axios.post("/timesheet/export", payload, { responseType: "blob" });
        const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/octet-stream" });
        let filename = `pontaj-${(props.employeeName || "angajat").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-_]/g, "")}-${formatAsIsoDate(rangeStart)}-${formatAsIsoDate(rangeEnd)}.${exportDialog.value.format === "xlsx" ? "xlsx" : "pdf"}`;
        const disposition = response.headers["content-disposition"];
        if (disposition) {
          const match = disposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) {
            filename = match[1];
          }
        }
        const link = document.createElement("a");
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        exportNotice.value = t("employeeTimesheetHr.export.notice", {
          format: exportDialog.value.format.toUpperCase(),
          start: formatAsIsoDate(rangeStart),
          end: formatAsIsoDate(rangeEnd),
        });
        exportRangeForm.value.start_date = formatAsIsoDate(rangeStart);
        exportRangeForm.value.end_date = formatAsIsoDate(rangeEnd);
        exportRangeActiveEdge.value = "start";
        exportDialog.value.open = false;
      } catch (error) {
        console.error("Eroare export pontaj:", error);
        const message = error?.response?.data?.error || t("employeeTimesheetHr.export.error");
        exportDialog.value.error = message;
      } finally {
        exportDialog.value.loading = false;
      }
    }

    return {
      events,
      orderedEvents,
      loading,
      errorMessage,
      exportNotice,
      selectedDate,
      formattedSelectedDate,
      calendarMenu,
      eventTypeOptions,
      dialog: dialog,
      canManage,
      title: cardTitle,
      t,
      timeFieldRule,
      timeMenu,
      timePickerValue,
      onTimeSelected,
      handleDateSelection,
      openExportDialog,
      exportDialog,
      exportRangeMenu,
      exportRangeDisplay,
      exportRangeDateModel,
      onExportRangeChange,
      handleExportRangePointerDown,
      handleExportRangeFieldFocus,
      handleExportRangeKeyboardOpen,
      handleExportRangeControlClick,
      exportFormats,
      confirmExport,
      formatEventTime,
      formatSource,
      eventTypeConfig,
      goToPreviousDay,
      goToNextDay,
      goToToday,
      openCreateDialog,
      openEditDialog,
      saveEvent,
      deleteEvent,
    };
  },
});
</script>

<style scoped>
.timesheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.timesheet-header__title {
  display: flex;
  align-items: center;
  gap: 4px;
}
.timesheet-header__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.timesheet-body {
  min-height: 220px;
}
.timesheet-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 12px;
  border: 1px dashed var(--ncrm-color-border-strong);
  border-radius: var(--app-radius-base);
  background-color: var(--ncrm-color-surface-muted);
}
.timesheet-empty__icon {
  opacity: 0.45;
}
.timesheet-list {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  padding: 4px;
}
.timesheet-list__item + .timesheet-list__item {
  border-top: 1px solid var(--ncrm-color-border-subtle);
}
.timesheet-item__meta {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.62);
}
</style>
